import { required, useTranslate, type Identifier } from "ra-core";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ReferenceArrayInput } from "@/components/admin/reference-array-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { NumberInput } from "@/components/admin/number-input";
import { DateInput } from "@/components/admin/date-input";
import { SelectInput } from "@/components/admin/select-input";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

import { useProjects } from "../projects/useCurrentProject";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";
import { AutocompleteContactInput } from "../contacts/AutocompleteContactInput.tsx";
import { firstOpenStage } from "./stages";
import { ResponsibleInput } from "../sales/ResponsibleInput";

/**
 * The deal form keeps only what a sales manager fills in (owner's rule 04.11):
 * name, company, contacts, amount, stage, expected date, account manager and
 * description. Categories are gone from the form; the column stays.
 */
export const DealInputs = ({
  chooseProject = false,
}: {
  /** ask for the project (a new deal while the switcher is on «Все проекты») */
  chooseProject?: boolean;
}) => {
  const isMobile = useIsMobile();
  return (
    <div className="flex flex-col gap-10 p-1">
      <DealInfoInputs />

      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <DealLinkedToInputs />
        <Separator orientation={isMobile ? "horizontal" : "vertical"} />
        <DealMiscInputs chooseProject={chooseProject} />
      </div>
    </div>
  );
};

const DealInfoInputs = () => {
  return (
    <div className="flex flex-col gap-4 flex-1">
      <TextInput source="name" validate={required()} helperText={false} />
      <TextInput source="description" multiline rows={3} helperText={false} />
    </div>
  );
};

const DealLinkedToInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4 flex-1">
      <h6 className="text-lg font-semibold">
        {translate("resources.deals.inputs.linked_to")}
      </h6>
      <ReferenceInput source="company_id" reference="companies">
        <AutocompleteCompanyInput
          label="resources.deals.fields.company_id"
          validate={required()}
          modal
        />
      </ReferenceInput>

      <ReferenceArrayInput source="contact_ids" reference="contacts_summary">
        <AutocompleteContactInput label="resources.deals.fields.contact_ids" />
      </ReferenceArrayInput>
    </div>
  );
};

const DealMiscInputs = ({ chooseProject }: { chooseProject: boolean }) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4 flex-1">
      <h6 className="text-lg font-semibold">
        {translate("resources.deals.field_categories.misc")}
      </h6>
      <NumberInput
        source="amount"
        defaultValue={0}
        helperText={false}
        validate={required()}
      />
      <DealStageInputs chooseProject={chooseProject} />
      <DateInput
        validate={required()}
        source="expected_closing_date"
        helperText={false}
        defaultValue={new Date().toISOString().split("T")[0]}
      />
      <ResponsibleInput />
    </div>
  );
};

/**
 * Stage of the deal: only the stages of the deal's pipeline. The pipeline is
 * asked for when the project has several; the project only when the form is
 * told so. Changing the project or pipeline puts the deal on the first open
 * stage of the new pipeline.
 */
const DealStageInputs = ({ chooseProject }: { chooseProject: boolean }) => {
  const { projects, pipelinesOf, stagesOf } = useProjects();
  const { setValue } = useFormContext();
  const projectId = useWatch({ name: "project_id" }) as Identifier | undefined;
  const pipelineId = useWatch({ name: "pipeline_id" }) as
    Identifier | undefined;
  const stageId = useWatch({ name: "stage_id" }) as Identifier | undefined;
  const pipelines = useMemo(
    () => (projectId != null ? pipelinesOf(projectId) : []),
    [projectId, pipelinesOf],
  );
  const stages = useMemo(() => stagesOf(pipelineId), [pipelineId, stagesOf]);

  // Keep the pipeline inside the project and the stage inside the pipeline.
  // These also run after the form resets its values (ra-core's Form resets
  // to its defaults on mount and whenever they change).
  useEffect(() => {
    if (projectId == null || !pipelines.length) return;
    if (!pipelines.some((p) => String(p.id) === String(pipelineId))) {
      setValue("pipeline_id", pipelines[0].id, { shouldDirty: true });
    }
  }, [projectId, pipelineId, pipelines, setValue]);

  useEffect(() => {
    if (!stages.length) return;
    if (!stages.some((s) => String(s.id) === String(stageId))) {
      setValue("stage_id", firstOpenStage(stages).id, { shouldDirty: true });
    }
  }, [stages, stageId, setValue]);

  return (
    <>
      {chooseProject ? (
        <SelectInput
          source="project_id"
          choices={projects}
          optionText="name"
          optionValue="id"
          helperText={false}
          validate={required()}
        />
      ) : null}
      {pipelines.length > 1 ? (
        <SelectInput
          source="pipeline_id"
          choices={pipelines}
          optionText="name"
          optionValue="id"
          helperText={false}
          validate={required()}
        />
      ) : null}
      <SelectInput
        source="stage_id"
        choices={stages}
        optionText="name"
        optionValue="id"
        helperText={false}
        validate={required()}
      />
    </>
  );
};
